import type { ReactElement } from 'react';
import { formatNumber } from '@xdocs/i18n';
import { Badge, Button, Modal, cn, useI18n } from '@xdocs/ui';
// Cost rows moved to `creditCostRows.ts` (kept verbatim) so this modal and
// the F48 `/pricing/credits` page render from ONE client-side source.
import { COST_ROWS } from './creditCostRows.js';
import { useSettingsBillingT } from './useSettingsBillingT.js';
import styles from './CreditsInfoModal.module.css';

export interface CreditsInfoModalProps {
  open: boolean;
  onClose: () => void;
  /** Closes the modal and hands off to the buy-credits flow (scrolls to `BuyCreditsGrid`). */
  onBuyCredits: () => void;
}

/** F48 "How credits work" explainer modal — static cost-matrix reference + a Buy credits CTA. */
export function CreditsInfoModal({ open, onClose, onBuyCredits }: CreditsInfoModalProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsBillingT();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('settingsBilling:creditsInfo.title')}
      description={t('settingsBilling:creditsInfo.description')}
      size="lg"
      scrollableBody
      footer={
        <Button variant="primary" onClick={onBuyCredits}>
          {t('settingsBilling:creditsInfo.buy')}
        </Button>
      }
    >
      <p className={cn(styles['eyebrow'])}>{t('settingsBilling:creditsInfo.eyebrow')}</p>
      <div className={cn(styles['tableWrap'])}>
        <table className={cn(styles['table'])}>
          <thead>
            <tr>
              <th scope="col">{t('settingsBilling:creditsInfo.column.action')}</th>
              <th scope="col">{t('settingsBilling:creditsInfo.column.standard')}</th>
              <th scope="col">{t('settingsBilling:creditsInfo.column.advanced')}</th>
              <th scope="col">{t('settingsBilling:creditsInfo.column.mastery')}</th>
            </tr>
          </thead>
          <tbody>
            {COST_ROWS.map((row) => (
              <tr key={row.nameKey}>
                <th scope="row">
                  <span className={cn(styles['actionName'])}>
                    {t(`settingsBilling:${row.nameKey}`)}
                    {row.pro && <Badge tone="pro">{t('settingsBilling:creditsInfo.badge.pro')}</Badge>}
                  </span>
                </th>
                <td>{formatNumber(locale, row.standard)}</td>
                <td>{formatNumber(locale, row.advanced)}</td>
                <td>{formatNumber(locale, row.mastery)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
